import React, { useState } from 'react'
import { get } from 'lodash'

import { config } from 'lib/config'
import { VERIFICATION_CODE_LENGTH } from 'utils/constants/input'
import { AuthManagerInstance } from 'utils/auth/AuthManager'
import { API } from 'utils/api/api'
import { Toastr } from 'utils/toastr/Toastr'
import { VerifyEmailModalView } from 'containers/createAccount/components/verifyEmailModal/VerifyEmailModalView'
import { FormFieldsData } from '../../../../types/form'

const REDIRECT_TIMEOUT = 3000

const redirectToHrisUiWithTimeout = () => {
  setTimeout(() => {
    window.location.href = `https://${config.API_BASE_URL}?shouldLogout=true`
  }, REDIRECT_TIMEOUT)
}

const isVerificationCodeValid = (code: string) =>
  !isNaN(parseInt(code, 10)) && code.toString().length === VERIFICATION_CODE_LENGTH

interface Props {
  formData: FormFieldsData
  isOpen: boolean
  setIsVerifyEmailModalOpen: (isOpen: boolean) => void
  handleSendNewCode: () => Promise<void>
}

export const useVerifyEmailModal = ({ formData, isOpen, setIsVerifyEmailModalOpen, handleSendNewCode }: Props) => {
  const email = formData?.email?.value
  const [isReady, setIsReady] = useState(false)
  const [isCodeWasSent, setIsCodeWasSent] = useState(true)
  const [isPending, setIsPending] = useState(false)
  const [verificationCode, setVerificationCode] = useState<string>()
  const handleHide = () => setIsVerifyEmailModalOpen(false)

  const handleInputChange = (value: string) => {
    setVerificationCode(value)
    setIsReady(isVerificationCodeValid(value))
  }

  const handleVerifyEmail = () => {
    setIsPending(true)

    // NOTE: This is needed so that verify email action is done without auth data
    AuthManagerInstance.clearAuthentication()

    API.makeRequest({
      type: 'post',
      requestUrl: '/auth/accounts/email-verify',
      requestData: {
        email,
        confirmationCode: verificationCode,
      },
    })
      .then(() => {
        Toastr.success('Account was successfully created.')

        redirectToHrisUiWithTimeout()
      })
      .catch(({ response }) => {
        const message = response && get(response, 'data.response.message')
        const errorMessage = message
          ? `Failed to verify code: ${message}`
          : 'Invalid verification code. Please, try again.'

        Toastr.error(errorMessage)
        setIsPending(false)
      })
  }

  const handleModalShow = () => setTimeout(() => setIsCodeWasSent(false), 2000)

  const handleModalHide = () => {
    setIsCodeWasSent(true)
    handleHide()
  }

  const sendNewCode = () => {
    setIsCodeWasSent(true)
    handleSendNewCode()
      .then(() =>
        Toastr.success(
          <span>
            A new verification code was sent to <b>{email}.</b>
          </span>,
        ),
      )
      .catch(({ response }) => {
        const message = response && get(response, 'data.response.message')
        const errorMessage = message
          ? `Failed to send verification code: ${message}`
          : 'Failed to send verification code.'

        Toastr.error(errorMessage)
      })
      .finally(() => setTimeout(() => setIsCodeWasSent(false), REDIRECT_TIMEOUT))
  }

  return {
    VerifyEmailModal: (
      <VerifyEmailModalView
        isOpen={isOpen}
        isReady={isReady}
        isPending={isPending}
        isCodeWasSent={isCodeWasSent}
        handleModalShow={handleModalShow}
        handleModalHide={handleModalHide}
        email={email}
        verificationCode={verificationCode}
        handleInputChange={handleInputChange}
        handleSendNewCode={sendNewCode}
        handleVerifyEmail={handleVerifyEmail}
      />
    ),
  }
}
